"use client";

import React from "react";
import { UserCheck, Coins, Zap, ShieldCheck, ChartBar as BarChart3, MessageSquare, Check, X, ArrowRight, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const comparisonPoints = [{
  id: 1,
  title: "Account Leadership",
  me: "Direct senior access to Yassine Essaadi for every strategic decision & audit.",
  agency: "Handed off to junior account executives immediately after pitching."
}, {
  id: 2,
  title: "Budget Efficiency",
  me: "100% of your retainer funds high-impact strategy and technical SEO execution.",
  agency: "A large portion covers glossy office rent, sales teams, and agency margin."
}, {
  id: 3,
  title: "Strategy Customisation",
  me: "Tailored organic roadmaps engineered around your exact CMS, tech stack, and goals.",
  agency: "Rigid, template-based monthly task checklists recycled across clients."
}, {
  id: 4,
  title: "Implementation Speed",
  me: "Fast turnarounds and direct collaboration with your internal developer team.",
  agency: "Slow ticketing systems, multiple review tiers, and delayed code updates."
}];
const keyBenefits = [{
  id: 1,
  title: "100% Senior Expert Attention",
  tagline: "Direct Consultant Access",
  description: "You work directly with me from day one. I conduct the technical audit, design the strategy, and execute the optimizations myself—never delegating your website's growth."
}, {
  id: 2,
  title: "Maximum Cost Efficiency",
  tagline: "Zero Agency Overhead",
  description: "Without account managers, sales reps, or lavish city-center offices to maintain, every pound you invest goes directly into revenue-generating SEO deliverables."
}, {
  id: 3,
  title: "Bespoke & Agile Strategy",
  tagline: "Tailored SEO Strategy",
  description: "Agencies rely on rigid monthly package templates. I engineer custom organic search roadmaps built around your unique market position, target audience, and architecture."
}, {
  id: 4,
  title: "Personal Accountability",
  tagline: "Honest Measurement",
  description: "My personal brand and professional reputation are on the line with every client. I hold myself directly accountable for sustainable ranking, traffic, and lead growth."
}, {
  id: 5,
  title: "Plain-English Reporting",
  tagline: "Transparent Reporting",
  description: "No automated 50-page PDF reports stuffed with vanity metrics. I deliver clear, actionable monthly summaries focused on keyword movements, organic traffic, and leads."
}, {
  id: 6,
  title: "Direct & Fast Communication",
  tagline: "Practical Implementation",
  description: "Need an urgent technical review or strategic advice? You message or call me directly without waiting through support queues or middle-management gatekeepers."
}];
export default function ConsultantBenefits() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.5
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const getBenefitIcon = (id: number) => {
    switch (id) {
      case 1:
        return <UserCheck className="size-5 text-accent" />;
      case 2:
        return <Coins className="size-5 text-accent" />;
      case 3:
        return <Zap className="size-5 text-accent" />;
      case 4:
        return <ShieldCheck className="size-5 text-accent" />;
      case 5:
        return <BarChart3 className="size-5 text-accent" />;
      case 6:
        return <MessageSquare className="size-5 text-accent" />;
      default:
        return <Sparkles className="size-5 text-accent" />;
    }
  };
  return <section id="consultantvsagencybenefits" data-nav="light" className="py-20 md:py-28 bg-background text-foreground relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs md:text-sm font-medium bg-secondary text-secondary-foreground border border-accent/30 rounded-full">
            <Sparkles className="size-3.5 mr-1.5 text-accent inline" />
            Independent SEO Consultant vs Agency
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-default mb-6">
            Why Work Directly With Me Instead of an SEO Agency?
          </h2>

          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed font-default">
            When you hire YE Consultancy, you work directly with me—Yassine Essaadi.
            I eliminate agency bloat, junior hand-offs, and rigid deliverables so 
            100% of your budget drives real search growth.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border-2 border-primary/20 bg-card text-card-foreground shadow-lg rounded-2xl relative overflow-hidden flex flex-col justify-between">
              <div className="bg-primary/5 border-b border-primary/10 px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">
                    Y
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-foreground font-default">
                      Working With Me
                    </h3>
                    <p className="text-xs text-muted-foreground font-default">
                      Independent Senior SEO Consultant
                    </p>
                  </div>
                </div>
                <Badge className="bg-primary text-primary-foreground border-none px-3 py-1 text-xs">
                  Direct Expert Access
                </Badge>
              </div>

              <CardContent className="p-6 md:p-8 space-y-6 flex-grow">
                {comparisonPoints.map((pt, i) => <div data-index={i} key={pt.id} className="space-y-1.5">
                    <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                      <Check className="size-4 shrink-0 text-accent" />
                      <span>{pt.title}</span>
                    </div>
                    <p className="text-sm text-foreground/90 pl-6 leading-relaxed font-default">
                      {pt.me}
                    </p>
                    {i < comparisonPoints.length - 1 && <Separator className="mt-4 opacity-50" />}
                  </div>)}
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border border-border bg-muted/40 text-card-foreground shadow-sm rounded-2xl flex flex-col justify-between opacity-90">
              <div className="bg-muted/80 border-b border-border px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-full bg-muted-foreground/20 text-muted-foreground flex items-center justify-center font-bold text-lg">
                    A
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground font-default">
                      Traditional SEO Agency
                    </h3>
                    <p className="text-xs text-muted-foreground font-default">
                      Standard Retainer Agency Model
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="text-muted-foreground text-xs">
                  Standard Agency
                </Badge>
              </div>

              <CardContent className="p-6 md:p-8 space-y-6 flex-grow">
                {comparisonPoints.map((pt, i) => <div data-index={i} key={pt.id} className="space-y-1.5">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <X className="size-4 shrink-0 text-destructive/80" />
                      <span>{pt.title}</span>
                    </div>
                    <p className="text-sm text-muted-foreground pl-6 leading-relaxed font-default">
                      {pt.agency}
                    </p>
                    {i < comparisonPoints.length - 1 && <Separator className="mt-4 opacity-50" />}
                  </div>)}
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        <div className="mb-12 text-center max-w-2xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-bold text-foreground font-default">
            6 Direct Advantages for Your Organic Growth
          </h3>
          <p className="text-sm md:text-base text-muted-foreground mt-2 font-default">
            Every engagement is crafted around hands-on SEO expertise, direct access, and clear business outcomes.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {keyBenefits.map((benefit, i) => <motion.div data-index={i} key={benefit.id} variants={itemVariants}>
              <Card className="h-full bg-card border border-border/80 hover:border-accent/50 transition-[border-color,box-shadow] duration-300 shadow-sm hover:shadow-md rounded-2xl flex flex-col justify-between group p-6">
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <div className="size-11 rounded-xl bg-secondary flex items-center justify-center border border-accent/20 group-hover:scale-105 transition-transform duration-300">
                      {getBenefitIcon(benefit.id)}
                    </div>
                    <Badge variant="secondary" className="bg-secondary/70 text-secondary-foreground text-xs font-normal border border-accent/20 rounded-full">
                      {benefit.tagline}
                    </Badge>
                  </div>

                  <h4 className="text-lg font-bold text-foreground font-default mb-2 group-hover:text-primary transition-colors">
                    {benefit.title}
                  </h4>

                  <p className="text-sm text-muted-foreground leading-relaxed font-default">
                    {benefit.description}
                  </p>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mt-16 md:mt-24">
          <div className="bg-gradient-to-br from-primary/5 via-secondary/40 to-background border border-accent/30 rounded-3xl p-8 md:p-12 shadow-lg text-center max-w-4xl mx-auto relative overflow-hidden">
            <div className="max-w-2xl mx-auto space-y-6 relative z-10">
              <Badge variant="outline" className="bg-background border-accent/40 text-foreground px-3 py-1 text-xs rounded-full">
                Personalized Partnership
              </Badge>

              <h3 className="text-2xl md:text-3xl font-bold text-foreground font-default">
                Ready to Experience Direct, High-Impact SEO Consultancy?
              </h3>

              <p className="text-muted-foreground text-base leading-relaxed font-default">
                {"I'm ready to review your website, analyze your competitors, and deliver a clear organic strategy tailored exclusively to your business goals."}
              </p>

              <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8 py-6 rounded-xl shadow-md w-full sm:w-auto">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation
                    <ArrowRight className="size-4 ml-2" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="border-border hover:bg-secondary/50 font-medium px-8 py-6 rounded-xl w-full sm:w-auto">
                  <Link to="/CaseStudiesPage">
                    Explore My Case Studies
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>

      </div>
    </section>;
}